// Assistant chat — used for both per-note chat and the general Ask AI page.
// Messages are persisted server-side by the assistantChat callable and
// streamed back via Firestore; dictation goes through transcribeVoiceMessage.
import {useEffect, useRef, useState, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import css from './note.module.css';
import pagesCss from '../../pages/pages.module.css';
import {Icon} from '../common/Icon';
import {IconButton, Markdown, Spinner} from '../common/ui';
import type {ChatMessage} from '../../types/Note';
import {
  subscribeNoteMessages,
  clearAssistantMessages,
} from '../../services/firestoreNotes';
import {
  assistantChat,
  transcribeVoiceMessage,
} from '../../services/functionsService';
import {useAuth} from '../../context/AuthProvider';
import {useToast} from '../../context/ToastProvider';
import {useConfirm} from '../../context/ConfirmProvider';
import {useUpgrade} from '../../context/UpgradeProvider';
import {
  useRecorder,
  recordingSupported,
  MicDeniedError,
} from '../../hooks/useRecorder';
import {getVoiceLanguage} from '../../services/settings';
import {blobToBase64} from '../../utils';
import {MAX_DICTATION_BASE64_BYTES} from '../../constants';
import posthog, {isPostHogConfigured} from '../../posthog';

interface Props {
  noteId: string | null;
  /** Rendered above the input when there are no messages yet. */
  emptyState?: ReactNode;
  /** Suggestion chips shown in the empty state; clicking prefills input. */
  suggestions?: string[];
}

export function ChatPanel({noteId, emptyState, suggestions}: Props) {
  const {t} = useTranslation();
  const {uid} = useAuth();
  const showToast = useToast();
  const confirm = useConfirm();
  const {handleLimitError} = useUpgrade();
  const recorder = useRecorder();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!uid) {
      return;
    }
    return subscribeNoteMessages(uid, noteId, incoming => {
      setMessages(incoming);
      // Server has persisted our question once it appears in the stream.
      setPending(current =>
        current && incoming.some(m => m.role === 'user' && m.content === current)
          ? null
          : current,
      );
    });
  }, [uid, noteId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, pending, thinking]);

  const send = async () => {
    const question = input.trim();
    if (!question || thinking) {
      return;
    }
    setInput('');
    setPending(question);
    setThinking(true);
    try {
      await assistantChat(noteId, question);
      if (isPostHogConfigured) {
        posthog.capture('assistant_question_sent');
      }
    } catch (e) {
      setPending(null);
      if (!handleLimitError(e)) {
        console.warn(e);
        showToast(t('ErrorFetch'), 'error');
      }
      setInput(question);
    } finally {
      setThinking(false);
    }
  };

  const toggleDictation = async () => {
    if (recorder.status === 'recording') {
      setTranscribing(true);
      try {
        const {blob} = await recorder.stop();
        const base64 = await blobToBase64(blob);
        if (base64.length > MAX_DICTATION_BASE64_BYTES) {
          showToast(t('upload_error'), 'error');
          return;
        }
        const result = await transcribeVoiceMessage(base64, getVoiceLanguage());
        setInput(current =>
          current ? `${current} ${result.text}` : result.text,
        );
        inputRef.current?.focus();
      } catch (e) {
        if (!handleLimitError(e)) {
          console.warn(e);
          showToast(t('ErrorFetch'), 'error');
        }
      } finally {
        setTranscribing(false);
      }
      return;
    }
    if (!recordingSupported()) {
      showToast(t('WebRecordingUnsupported'), 'error');
      return;
    }
    try {
      await recorder.start();
    } catch (e) {
      showToast(
        e instanceof MicDeniedError
          ? t('WebMicDenied')
          : t('Error starting recording'),
        'error',
      );
    }
  };

  const clearChat = async () => {
    if (!uid || messages.length === 0) {
      return;
    }
    const ok = await confirm({
      title: t('ClearConversation'),
      message: t('ClearConversationConfirm'),
      confirmText: t('Delete'),
      danger: true,
    });
    if (ok) {
      await clearAssistantMessages(uid, noteId);
    }
  };

  const empty = messages.length === 0 && !pending;

  return (
    <div className={css.chat}>
      <div className={css.chatScroll} ref={scrollRef}>
        {empty && emptyState}
        {empty && suggestions && suggestions.length > 0 && (
          <div className={pagesCss.chipRows}>
            <div className={pagesCss.chipRow}>
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion}
                  type="button"
                  className={pagesCss.chip}
                  style={{animationDelay: `${120 + index * 60}ms`}}
                  onClick={() => {
                    setInput(suggestion);
                    inputRef.current?.focus();
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map(message =>
          message.role === 'user' ? (
            <div key={message.id} className={css.bubbleUser}>
              {message.content}
            </div>
          ) : (
            <div key={message.id} className={css.bubbleAi}>
              <Markdown>{message.content}</Markdown>
            </div>
          ),
        )}
        {pending && <div className={css.bubbleUser}>{pending}</div>}
        {thinking && (
          <div className={css.bubbleAi} aria-label={t('AIThinking')}>
            <div className={css.typingRows}>
              <div className={css.typingRow} />
              <div className={css.typingRowShort} />
            </div>
          </div>
        )}
      </div>
      <div className={`${css.chatInputBar} no-print`}>
        {messages.length > 0 && (
          <IconButton
            icon="trash"
            title={t('ClearConversation')}
            onClick={clearChat}
          />
        )}
        <textarea
          ref={inputRef}
          className={css.chatInput}
          placeholder={
            recorder.status === 'recording'
              ? t('Recording')
              : t('TypeYourMessage')
          }
          value={input}
          rows={1}
          onChange={e => {
            setInput(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          disabled={thinking && !input}
        />
        <button
          className={
            recorder.status === 'recording' ? css.chatMicRecording : css.chatMic
          }
          onClick={toggleDictation}
          disabled={transcribing}
          title={t('Record Audio')}
          type="button"
        >
          {transcribing ? <Spinner /> : <Icon name="mic" size={18} />}
        </button>
        <button
          className={css.chatSend}
          onClick={send}
          disabled={!input.trim() || thinking}
          title={t('Done')}
          type="button"
        >
          <Icon name="send" size={17} />
        </button>
      </div>
    </div>
  );
}
